import '../styles/RequestManagement.css';
import '../styles/Beneficiaries.css';
import GenericList from './GenericList';
import GenericSearch from './GenericSearch.js';
import { useEffect, useState, useCallback } from 'react';
import { updateDigitalRequestApi, getAllDigitalRequestsApi, deleteDigitalRequestApi } from '../api/digital_request_api';
import { getBeneficiaryFromRequest } from '../utils/fetchFromUrl.js';
import { add_beneficiary } from '../api/beneficiary_api.js'
import { DateTime } from "luxon";
import { useNavigate, useSearchParams } from 'react-router-dom';

const RequestManagement = () => {
    const [requests, setRequests] = useState([]);
    const [editId, setEditId] = useState(null);
    const [editComment, setEditComment] = useState('');
    const [loading, setLoading] = useState(false);
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const searchTerm = searchParams.get('search') || '';
    const searchKeyType = searchParams.get('searchKey') || 'beneficiary_name';
    const setSearchTerm = (val) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('search', val); return p; }, { replace: true });
    const setSearchKeyType = (val) => setSearchParams(prev => { const p = new URLSearchParams(prev); p.set('searchKey', val); return p; }, { replace: true });

    useEffect(() => {
        async function fetchRequests() {
            setLoading(true);
            try {
                const data = await getAllDigitalRequestsApi();

                const normalizedRequests = data.map(r => ({
                    ...r,
                    beneficiary_name: `${r.beneficiary_first_name || ''} ${r.beneficiary_last_name || ''}`,
                    applicant_name: `${r.applicant_first_name || ''} ${r.applicant_last_name || ''}`,
                    submission_date: r.submission_date
                }));

                setRequests(normalizedRequests);
            } catch (err) {
                setRequests([]);
                alert('שגיאה בטעינת הבקשות');
            } finally {
                setLoading(false);
            }
        }
        fetchRequests();
    }, []);

    const handleDelete = useCallback(async (id) => {
        if (!id) return;
        if (!window.confirm('האם אתה בטוח שברצונך למחוק את הבקשה?')) return;
        setLoading(true);
        try {
            await deleteDigitalRequestApi(id);
            setRequests(prev => prev.filter(r => r.id !== id));
        } catch (err) {
            alert('מחיקת הבקשה נכשלה');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleSaveCommentInline = useCallback(async (item, newComment) => {
        if (!item?.id) return;
        setEditComment(newComment);
        setLoading(true);
        try {
            await updateDigitalRequestApi(item.id, { admin_comments: newComment });
            setEditId(null);
            setRequests(prev => prev.map(r => r.id === item.id ? { ...r, admin_comments: newComment } : r));
        } catch (err) {
            alert('שמירת הערה נכשלה');
        } finally {
            setLoading(false);
        }
    }, []);

    const handleStatusChange = useCallback(async (id, newStatus) => {
        if (!id || !newStatus) return;
        setLoading(true);
        try {
            if (newStatus === 'Rejected') {
                try {
                    await updateDigitalRequestApi(id, { status: newStatus });
                    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
                } catch (e) {
                    alert('עדכון הסטטוס נכשל');
                }
            } else if (newStatus === 'Approved') {
                try {
                    const beneficiary = await getBeneficiaryFromRequest(id);
                    await add_beneficiary(beneficiary);
                    
                    await updateDigitalRequestApi(id, { status: newStatus });
                    setRequests(prev => prev.map(r => r.id === id ? { ...r, status: newStatus } : r));
                    alert('פרטי הנתמך נוספו לרשימת הנתמכים בהצלחה');
                } catch (e) {
                    if (e?.response?.status === 409 || (typeof e?.message === 'string' && e.message.includes('ת.ז. כבר קיים'))) {
                        alert('ישנו נתמך עם ת.ז. זהה במערכת, הנתמך לא נוסף לרשימת הנתמכים.');
                    } else {
                        alert('הבקשה אושרה אך אירעה שגיאה בהוספת הנתמך לרשימת הנתמכים');
                    }
                }
            }
        } catch (err) {
            alert('עדכון הסטטוס נכשל');
        } finally {
            setLoading(false);
        }
    }, []); // הסרתי את navigate כי הוא אינו בשימוש בתוך הפונקציה

        const truncate = (text, max = 60) => {
        if (!text) return '';
        return text.length > max ? text.slice(0, max) + '…' : text;
    };

    const columns = [
        { key: 'applicant_name', label: 'שם מבקש', render: item => `${item.applicant_first_name || ''} ${item.applicant_last_name || ''}` },
        { key: 'beneficiary_name', label: 'שם נתמך', render: item => `${item.beneficiary_first_name || ''} ${item.beneficiary_last_name || ''}` },
        { 
            key: 'submission_date', 
            label: 'תאריך הגשה', 
            render: item => item.submission_date ? DateTime.fromJSDate(new Date(item.submission_date)).toFormat('dd.MM.yyyy') : '' 
        },
        {
            key: 'admin_comments',
            label: 'הערות מנהל',
            render: item => (
                editId === item.id ? (
                    <textarea
                        value={editComment}
                        autoFocus
                        onChange={e => setEditComment(e.target.value)}
                        onBlur={e => {
                            if (e.target.value !== (item.admin_comments || '')) {
                                handleSaveCommentInline(item, e.target.value);
                            } else {
                                setEditId(null);
                            }
                        }}
                        className="admin-comment-textarea"
                    />
                ) : (
                    <span
                        className="admin-comment-span"
                        title={item.admin_comments || ''}
                        onClick={() => {
                            setEditId(item.id);
                            setEditComment(item.admin_comments || '');
                        }}
                    >
                        {item.admin_comments
                            ? truncate(item.admin_comments, 60)
                            : <span className="admin-comment-placeholder">הוסף הערה</span>}
                    </span>
                )
            )
        },
        {
            key: 'view',
            label: 'צפייה',
            render: item =>
                <a href={`/digital-form/view/${item.id}`} target="_blank" rel="noopener noreferrer">הצג טופס מלא</a>
        },
        {
            key: 'status',
            label: 'סטטוס',
            render: item => (
                item.status === 'Pending' ? (
                    <select
                        value={item.status}
                        onChange={e => handleStatusChange(item.id, e.target.value)}
                        className="status-select"
                    >
                        <option value="Pending">ממתין</option>
                        <option value="Approved">מאושר</option>
                        <option value="Rejected">נדחה</option>
                    </select>
                ) : (
                    <span className={`status-label ${item.status === 'Approved' ? 'status-approved' : 'status-rejected'}`}>
                        {item.status === 'Approved' ? 'מאושר' : item.status === 'Rejected' ? 'נדחה' : item.status}
                    </span>
                )
            )
        },
        {
            key: 'actions',
            label: 'פעולות',
            render: item => (
                <>
                    <button className="icon-btn" title="מחק" onClick={() => handleDelete(item.id)}>
                        <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M3 5.5H17M7 5.5V4C7 3.44772 7.44772 3 8 3H12C12.5523 3 13 3.44772 13 4V5.5M9 8.5V15.5M11 8.5V15.5M5 5.5H15L16 17C16 17.5523 15.5523 18 15 18H5C4.44772 18 4 17.5523 4 17L5 5.5Z" />
                        </svg>
                    </button>
                </>
            )
        }
    ];

    return (
        <div className="requests-page-container">
            <div className="top-buttons-container" style={{ marginBottom: "1.25rem", display: 'flex', gap: '10px', padding: '15px' }}>
                <button className="round-nav-btn" onClick={() => navigate(-1)} title="חזרה">
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="#EF5DA8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <line x1="5" y1="12" x2="19" y2="12"></line>
                        <polyline points="12 5 19 12 12 19"></polyline>
                    </svg>
                </button>
                <button className="round-nav-btn" onClick={() => navigate('/main_screen')} title="דף הבית">
                    <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="#EF5DA8" strokeWidth="2">
                        <path d="M12 3L2 12h3v7h4v-5h6v5h4v-7h3L12 3z" />
                    </svg>
                </button>
            </div>
            <div className="beneficiaries-container" style={{ marginTop: "4rem" }}>
                <div className='header'>
                    <h1 className="title">ניהול בקשות</h1>
                    <GenericSearch
                        searchTerm={searchTerm}
                        setSearchTerm={setSearchTerm}
                        searchKeyType={searchKeyType}
                        setSearchKeyType={setSearchKeyType}
                        searchFields={[
                            { key: 'beneficiary_name', label: 'שם נתמך' },
                            { key: 'applicant_name', label: 'שם מבקש' },
                            { key: 'submission_date', label: 'תאריך הגשה' },
                            { key: 'status', label: 'סטטוס' },
                            { key: 'admin_comments', label: 'הערות מנהל' }
                        ]}
                    />
                </div>
                <GenericList
                    data={requests}
                    columns={columns}
                    searchTerm={searchTerm}
                    searchKeyType={searchKeyType}
                    emptyText="אין בקשות להצגה"
                />
            </div>
        </div>
    );
};

export default RequestManagement;
